import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Form, Input, Space, Spin, Typography } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { useServerNotifications } from "../api/notifications";
import type { CredentialsResponse, CredentialsUpdateResponse } from "../api/types";

interface CredentialsForm {
  email: string;
  current_password: string;
  password: string;
  password_confirmation: string;
}

/**
 * The login credentials of the own account: the e-mail address used to sign in
 * and the password.
 *
 * Both live behind the same endpoint because Devise's update_with_password
 * demands the current password for either of them — which is also why neither
 * is settable through the personal settings (PUT /main/settings).
 *
 * The JWT is keyed on the jti rather than on the credentials, so changing them
 * does not end the session; the user stays signed in.
 */
export function CredentialsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CredentialsForm>();

  const credentials = useQuery({
    queryKey: ["main", "password"],
    queryFn: () => apiFetch<CredentialsResponse>("/main/password"),
  });

  // The address is the one field that starts filled — it is being edited, not
  // entered.
  useEffect(() => {
    if (credentials.data) {
      form.setFieldsValue({ email: credentials.data.data.email });
    }
  }, [credentials.data, form]);

  const update = useMutation({
    mutationFn: (values: CredentialsForm) =>
      apiFetch<CredentialsUpdateResponse>("/main/password", {
        method: "PUT",
        body: {
          email: values.email,
          current_password: values.current_password,
          // Left out entirely when empty: the server reads "a password was
          // requested" from its presence.
          ...(values.password
            ? {
                password: values.password,
                password_confirmation: values.password_confirmation,
              }
            : {}),
        },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      // Never keep the entered password around; the address stays, because the
      // answer reports what is now stored.
      form.setFieldsValue({ current_password: "", password: "", password_confirmation: "" });
      void queryClient.invalidateQueries({ queryKey: ["main", "password"] });
    },
    onError: (error: unknown) => {
      // Validation errors arrive as notifications in the user's language; only
      // a transport failure has nothing to show.
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  if (credentials.isPending) return <Spin />;

  if (credentials.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={
          credentials.error instanceof Error
            ? credentials.error.message
            : String(credentials.error)
        }
      />
    );
  }

  const { unconfirmed_email: unconfirmedEmail, password_length: passwordLength } =
    credentials.data.data;

  return (
    <Card title={t("auth.edit_account")} style={{ maxWidth: 640 }}>
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        {unconfirmedEmail && (
          // Same wording the server uses when it accepts the change, so the
          // state stays understandable on a later visit to this screen.
          <Alert
            type="info"
            showIcon
            title={t("user.email_confirmation_pending", { email: unconfirmedEmail })}
          />
        )}

        <Form<CredentialsForm>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => update.mutate(values)}
        >
          <Form.Item
            name="email"
            label={t("activerecord.attributes.user.email")}
            rules={[{ required: true, type: "email" }]}
          >
            <Input autoComplete="username" />
          </Form.Item>

          <Form.Item
            name="current_password"
            label={t("activerecord.attributes.user.current_password")}
            // Required for either change — the server verifies it before it
            // touches anything.
            rules={[{ required: true }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            name="password"
            label={t("activerecord.attributes.user.password")}
            // The length rule is the server's own (Devise.password_length);
            // antd phrases the violation in the active language.
            rules={[{ min: passwordLength.min, max: passwordLength.max }]}
            extra={
              <Typography.Text type="secondary">
                {t("user.hints.password_confirmation")}
              </Typography.Text>
            }
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="password_confirmation"
            label={t("activerecord.attributes.user.password_confirmation")}
            dependencies={["password"]}
            rules={[
              ({ getFieldValue }) => ({
                validator: (_rule, value: string) =>
                  value === getFieldValue("password")
                    ? Promise.resolve()
                    : Promise.reject(new Error(t("activerecord.errors.messages.confirmation"))),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={update.isPending}>
            {t("form.update")}
          </Button>
        </Form>
      </Space>
    </Card>
  );
}
